import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    Input, OnChanges,
    ViewChild,
    SimpleChanges,
    input,
    output,
    computed,
    Signal,
} from '@angular/core';
import {
    IActionMapping,
    ITreeOptions,
    ITreeState,
    TREE_ACTIONS,
    TreeModel,
    TreeNode,
} from '@ali-hm/angular-tree-component';

export interface KuiTreeSelectNode {
    id: string;
    name: string;
    icon?: string;
    color?: string;
    children?: KuiTreeSelectNode[];
    hasChildren?: boolean;
    getChildren?: (node: KuiTreeSelectNode) => Promise<KuiTreeSelectNode[]>;
    node?: {
        realParent?: {
            [property: string]: any;
        };
        data: {
            [property: string]: any;
        };
    };
    eventName?: string;
    data?: any;
    active?: boolean;
}

const singleSelectActionMap: IActionMapping = {
    mouse: {
        click(tree, node, $event) {
            if (node.hasChildren) {
                TREE_ACTIONS.TOGGLE_EXPANDED(tree, node, event);
            } else {
                TREE_ACTIONS.TOGGLE_ACTIVE(tree, node, $event);
            }
        },
    },
};

@Component({
    selector: 'kui-tree-select',
    templateUrl: 'tree-select.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class KuiTreeSelectComponent implements AfterViewInit, OnChanges {
    singleSelect = input<boolean>(false);
    nodes = input.required<KuiTreeSelectNode[]>();

    // angular-tree-component v21 all nodes need to have a children property even if an empty array
    updatedNodes: Signal<KuiTreeSelectNode[]> = computed(() => this.normalizeTree(this.nodes()));

    selectedNodeChanged = output<KuiTreeSelectNode>();

    stateValue: ITreeState;
    stateChange = output<ITreeState>();
    
    @Input()
    get state() {
        return this.stateValue;
    }
    set state(value: ITreeState) {
        this.stateValue = value;
        this.stateChange.emit(value);
        this.selectedChange.emit(this.selected);
    }

    selectedChange = output<string>();
    selectedNodeId: string;

    @Input()
    set selected(value: string) {
        this.selectedNodeId = value;
        setTimeout(() => {
            if (this.treeModel && value) {
                const node = this.treeModel.getNodeById(value);
                if (node) {
                    node.setActiveAndVisible();
                }
            }
        });
    }
    get selected(): string {
        return this.stateValue && this.stateValue.activeNodeIds && Object.keys(this.stateValue.activeNodeIds).filter(x => this.stateValue.activeNodeIds[x])[0];
    }

    @ViewChild('tree') tree: any;

    treeModel: TreeModel;
    options: ITreeOptions = {
        getChildren: (node: TreeNode): Promise<KuiTreeSelectNode[]> => {
            if (node.data.getChildren) {
                return node.data.getChildren(node.data);
            }
            return Promise.resolve([]);
        },
    };

    ngAfterViewInit() {
        this.treeModel = this.tree.treeModel;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.singleSelect && this.singleSelect()) {
            this.options.actionMapping = singleSelectActionMap;
        }
        if (changes.nodes && Array.isArray(this.nodes())) {            
            if (this.nodes().length === 1) {
                setTimeout(() => {
                    this.treeModel.expandAll();
                });
            }
            if (this.selectedNodeId) {
                setTimeout(() => {
                    const node = this.treeModel.getNodeById(this.selectedNodeId);
                    if (node) {
                        node.setActiveAndVisible();
                    }
                });
            }
        }
    }

    clearSelection() {
        this.treeModel.setFocus(false);
        this.treeModel.collapseAll();

        this.treeModel.setState({
            selectedNodeIds: [],
            activeNodeIds: [],
            expandedNodeIds: [],
            selectedLeafNodeIds: [],
            focusedNodeId: null,
        });
    }

    onToggle(node: any, event: MouseEvent) {
        // if inside a popup/dropdown menu this prevents it from closing it
        // when clicking the toggle icon
        event.stopPropagation();
        node.toggleExpanded();
    }

    normalizeTree(nodes: any[]): any[] {
        if (!Array.isArray(nodes)) return [];
      
        return nodes.map(node => this.normalizeNode(node));
    }
      
    private normalizeNode(node: any): any {
        if (!Array.isArray(node.children)) return node;
        
        return {
            ...node,
            children: node.children.map(child => this.normalizeNode(child))
        };
    }
      
}
